/* import __COLOCATED_TEMPLATE__ from './sq-field-input-searchable.hbs'; */
/**
## Field Input Searchable

`sq-field-input-searchable` is a variant on a standard `sq-field-input`,
which adds a type-ahead search for the input field. It also adds support for you to...

1) load data in a paginated manner
2) perform custom filtering, including remote or async API based search

In addition to the usual mouse clicks to select options, the component is also fully
functional with the keyboard. In addition to requisite type-ahead for narrowing options,
users can also use the up/down keys to cycle between options, enter to commit, and escape to revert.

```example
@title Basic Example in Form Field

@controller-properties
{
  // In actual code, use a computed property and make this this.get('options')[0]
  selectedValue: { label: 'Alpha', value: 0 },

  options: [
    { label: 'Alpha', value: 0 },
    { label: 'Bar', value: 1 },
    { label: 'Beta', value: 2 },
    { label: 'Bonn', value: 3 },
    { label: 'Charlie', value: 4 },
    { label: 'Delta', value: 5 }
  ]
}

@hbs
<div class="form-table">
  <div class="form-row">
    {{#sq-form-field label="Option"}}
      {{#sq-field-input-searchable options=options
                                   value=selectedValue
                                   placeholder="Sample Placeholder"
                                   valueSelected=(action (mut selectedValue)) as |searchable|}}
        {{searchable.input}}
        {{#searchable.list as |option|}}
          {{option.label}}
        {{/searchable.list}}
      {{/sq-field-input-searchable}}
    {{/sq-form-field}}
  </div>
</div>
<br />
<span>Selected value: {{selectedValue.label}}</span>
```

```example
@title Example with fluid width

@controller-properties
{
  // In actual code, use a computed property and make this this.get('options')[0]
  selectedValue: { label: 'Alpha', value: 0 },

  options: [
    { label: 'Alpha', value: 0 },
    { label: 'Bar', value: 1 },
    { label: 'Beta', value: 2 },
    { label: 'Bonn', value: 3 },
    { label: 'Charlie', value: 4 },
    { label: 'Delta', value: 5 }
  ]
}

@hbs
<div class="form-table">
  <div class="form-row">
    {{#sq-form-field label="Option"}}
      {{! By default, the component style has a fixed width for the options. Setting class to "form-field-input-searchable--is-fluid-width" changes this so the width becomes 100%. }}
      {{#sq-field-input-searchable options=options
                                   value=selectedValue
                                   placeholder="Sample Placeholder"
                                   valueSelected=(action (mut selectedValue))
                                   class="form-field-input-searchable--is-fluid-width" as |searchable|}}
        {{searchable.input}}
        {{#searchable.list as |option|}}
          {{option.label}}
        {{/searchable.list}}
      {{/sq-field-input-searchable}}
    {{/sq-form-field}}
  </div>
</div>
<br />
<span>Selected value: {{selectedValue.label}}</span>
```

```example
@title External filtering - case sensitive search - resets when the filter is emptied

@controller-properties
{
  // In actual code, use a computed property or set in an init function and make this this.get('options')[0]
  selectedValue: { label: 'Alpha', value: 0 },

  // In actual code, use a computed property or set in an init function and make this this.get('options')
  originalOptions: [
    { label: 'Alpha', value: 0 },
    { label: 'Bar', value: 1 },
    { label: 'Beta', value: 2 }
  ],

  options: [
    { label: 'Alpha', value: 0 },
    { label: 'Bar', value: 1 },
    { label: 'Beta', value: 2 }
  ],

  actions: {
    inputChanged(filter) {
      if (!filter.length) {
        this.set('options', this.get('originalOptions'));
        return;
      }

      const filteredOptions = this.get('options').filter(option => {
        return option.label.slice(0, filter.length) === filter;
      });

      this.set('options', filteredOptions);
    }
  }
}

@hbs
<div class="form-table">
  <div class="form-row">
    {{#sq-form-field label="Option"}}
      {{#sq-field-input-searchable options=options
                                   value=selectedValue
                                   placeholder="Sample Placeholder"
                                   valueSelected=(action (mut selectedValue))
                                   inputChanged=(action 'inputChanged') as |searchable|}}
        {{searchable.input}}
        {{#searchable.list as |option|}}
          {{option.label}}
        {{/searchable.list}}
      {{/sq-field-input-searchable}}
    {{/sq-form-field}}
  </div>
</div>
<br />
<span>Selected value: {{selectedValue.label}}</span>
```

```example
@title Additive Option - Default
@description Add an option to the select that will trigger an action to create a new option.

@controller-properties
{
  // In actual code, use a translation
  createNewOptionText: 'Create New',

  options: [
    { label: 'Alpha', value: 0 },
    { label: 'Bar', value: 1 },
    { label: 'Beta', value: 2 }
  ],

  actions: {
    createNewOption(filter) {
      const optionsLength = this.get('options.length');
      this.get('options').push({ label: 'New Option', value: optionsLength });
    }
  }
}

@hbs
<div class="form-table">
  <div class="form-row">
    {{#sq-form-field label="Option"}}
      {{#sq-field-input-searchable options=options
                                   value=selectedValue
                                   placeholder="Sample Placeholder"
                                   isAdditive=true
                                   valueSelected=(action (mut selectedValue))
                                   createNewOption=(action 'createNewOption') as |searchable|}}
        {{searchable.input}}
        {{#searchable.list as |option|}}
          {{#if option.isAdditive}}
            {{createNewOptionText}}
          {{else}}
            {{option.label}}
          {{/if}}
        {{/searchable.list}}
      {{/sq-field-input-searchable}}
    {{/sq-form-field}}
  </div>
</div>
<br />
<span>Selected value: {{selectedValue.label}}</span>
```

```example
@title Additive Option - Replace filter text
@description Add an option to the select that will trigger an action to create a new option.
  If a string is returned by the createNewOption action, it will replace the filter text.

@controller-properties
{
  // In actual code, use a translation
  createNewOptionText: 'Create New',

  options: [
    { label: 'Alpha', value: 0 },
    { label: 'Bar', value: 1 },
    { label: 'Beta', value: 2 }
  ],

  actions: {
    createNewOption(filter) {
      const optionsLength = this.get('options.length');
      this.get('options').push({ label: 'New Option', value: optionsLength });
      return '';
    }
  }
}

@hbs
<div class="form-table">
  <div class="form-row">
    {{#sq-form-field label="Option"}}
      {{#sq-field-input-searchable options=options
                                   value=selectedValue
                                   placeholder="Sample Placeholder"
                                   isAdditive=true
                                   valueSelected=(action (mut selectedValue))
                                   createNewOption=(action 'createNewOption') as |searchable|}}
        {{searchable.input}}
        {{#searchable.list as |option|}}
          {{#if option.isAdditive}}
            {{createNewOptionText}}
          {{else}}
            {{option.label}}
          {{/if}}
        {{/searchable.list}}
      {{/sq-field-input-searchable}}
    {{/sq-form-field}}
  </div>
</div>
<br />
<span>Selected value: {{selectedValue.label}}</span>
```

 ```example
 @title Example in Form Field with Options Grouped Under Headings
 @description Groups will be sorted by "no group" first, and then in the order of each groupBy occured.
   In the following example, the headers will be in order of: [undefined, 'Mineral, 'Animal', 'Vegetable'].

 @controller-properties
 {

   options: [
     { label: 'Bonn', value: 3, type: 'Mineral' },
     { label: 'Alpha', value: 0, type: 'Animal' },
     { label: 'Bar', value: 1, type: 'Animal' },
     { label: 'Beta', value: 2, type: 'Vegetable' },
     { label: 'Charlie', value: 4, type: 'Mineral' },
     { label: 'Delta', value: 5, type: 'Mineral' },
     { label: 'Howard', value: 56, type: undefined }
   ]
 }

 // GroupOptionsBy can also reference a deeply nested property on a model -- eg. "model.someThing.type"
 // Options missing the header property or with a falsy value will be floated to the top
 @hbs
 <div class="form-table">
   <div class="form-row">
     {{#sq-form-field label="Option"}}
       {{#sq-field-input-searchable options=options
                                    value=selectedValue
                                    placeholder="Sample Placeholder"
                                    groupOptionsBy="type"
                                    valueSelected=(action (mut selectedValue)) as |searchable|}}
         {{searchable.input}}
         {{#searchable.list as |option|}}
            {{option.label}}
         {{/searchable.list}}
       {{/sq-field-input-searchable}}
     {{/sq-form-field}}
   </div>
 </div>
 <br />
 <span>Selected value: {{selectedValue.label}}</span>
 ```

### Hash Properties
This component exposes internal properties as a hash.

@param {Component} input - component used to render the input
@param {Component} list - component used to render the list of options
@param {Boolean} isActive - indicates whether the component is in an active (clicked) state.
@param {Boolean} renderOptionsOnlyWhenOpen - flag to hide list options unless isOpen is true

### Parameters
@param {Array} options - Required - pass in an array of objects. Each object should have a single property that can be used as a label for searching
@param {Object} value - Required - pass in a single object for preselection. Pass null if you don't have anything yet. As long as the filtering hooks up, you should see the filter automatically change
@param {Action} valueSelected - Required - or there's little point to this component. It is called when the user selects an option (or types in a new one)

@param {Boolean} standaloneStyling - Optional - set to true to add on styling modifiers to make the input look good standalone outside of a form field
@param {Boolean} isLoading - Optional - set to true when loading is in progress in the parent component - shows a loading spinner at the bottom of the list
@param {Boolean} allowDisabled - Optional - set to true to make options with 'isDisabled' as true be unselectable in the dropdown. Also applies 'form-field-input-searchable__option--disabled' class to disabled options
@param {String} labelPath - Optional - the property of each object that the component should key off in terms of its label - defaults to `label`
@param {String} placeholder - Optional - the placeholder to use
@param {String} maxlength - Optional -  the maximum number of characters to allow in the input field
@param {String} nullStateText - Optional - this text will appear in the list when nothing is matched
@param {String} ariaDescribedBy - Optional - populates aria-describedby attribute on the input element
@param {Object} defaultOption - Optional - when passed in, pressing "enter" when no option is selected will send this option. The first option will no longer be pre-selected.
@param {Boolean} shouldHighlightDefaultOption - Optional - highlights the default option on open when this is true
@param {Boolean} isAdditive - Optional - adds an always-present option to create a new instance of the options being selected. Note that this will prevent showing any null state text.
@param {String} groupOptionsBy - Optional - string matching property name to be used as headings to group options under
@param {Object} formatter - Optional - when passed in, the user input string, as well as the option labels will be formatted according to the 'apply' function, the formatter will not make user visible changes.
@param {Boolean} [alwaysOpenIfActive] - Optional - If true, opens list element as long as isActive is true
@param {Boolean} [syncFilterOnClose=true] - Optional - If true (the default), the filter will reset to represent the passed in value when the dropdown is closed and neither `valueSelected` nor `inputCancelled` were sent.
@param {Boolean} [clearFilterOnClose=false] - Optional - If true, the input value (filter) will be cleared when the dropdown list closes
@param {Boolean} [scrollNearest=false] - Optional - If true, the scroll event resulting from navigating down the options with the keyboard will be limited to the nearest parent (useful in modals)

@param {Action} inputChanged - Optional - an action called when the user has typed something. It has one parameter - the currently entered filter string from the input. Use this information to load more data lazily from an API or anything else you may need to do
@param {Action} inputCancelled - Optional - called when the user hits escape after opening the dropdown
@param {Action} dropdownOpened - Optional - called when the dropdown opens
@param {Action} dropdownClosed - Optional - called when the dropdown closes
@param {Action} dropdownScrolledToBottom - Optional - called when the dropdown is scrolled to the bottom. This is useful when the parent component wants to load more items
@param {Action} componentBlurred - Optional - called when the user clicked on the document DOM outside the dropdown
@param {Action} createNewOption - Optional - called when the user clicks on the additive option. If a string is returned by this action, it will replace the filter text. If a Promise is returned by this action, it's resolved value will replace the filter text. To be used in conjunction with 'isAdditive'.

Sass dependencies at: `app/assets/stylesheets/bridgeport/components/form-elements/formfield/_formfield-input-searchable.sass`
*/

import { schedule, scheduleOnce } from '@ember/runloop';
import { reads, not } from '@ember/object/computed';
import { isEmpty, isBlank, isPresent, isNone } from '@ember/utils';
import { computed, get, set } from '@ember/object';
import Component from '@ember/component';
import { resolve } from 'rsvp';
import FieldElementSupport from '../utils/field-element-support';
const KEY_CODE_TAB = 9;
const KEY_CODE_ENTER = 13;
const KEY_CODE_ESC = 27;
const KEY_CODE_UP = 38;
const KEY_CODE_DOWN = 40;
const KEY_CODE_META = 91;
const KEY_CODE_CONTROL = 17;
const KEY_CODE_SHIFT = 16;

// Adapted from Ember.Array.uniq
function uniq(array) {
  const ret = [];
  const seen = new Set();
  for (const item of array) {
    if (!seen.has(item)) {
      seen.add(item);
      ret.push(item);
    }
  }
  return ret;
}

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend(FieldElementSupport, {
  // saved refs to event handlers for easy removal
  boundHandleScroll: null,
  // passed in (required)
  options: null,
  value: null,
  // passed in (optional)
  standaloneStyling: false,
  isLoading: false,
  labelPath: 'label',
  placeholder: null,
  nullStateText: null,
  ariaDescribedBy: null,
  defaultOption: null,
  isAdditive: false,
  groupOptionsBy: null,
  formatter: null,
  syncFilterOnClose: true,
  alwaysOpenIfActive: false,
  clearFilterOnClose: false,
  scrollNearest: false,
  // actions passed in (required)
  valueSelected: null,
  // actions passed in (optional)
  inputChanged: null,
  inputCancelled: null,
  dropdownOpened: null,
  dropdownClosed: null,
  dropdownScrolledToBottom: null,
  classNames: ['form-field-input-searchable'],
  classNameBindings: ['isActive:form-field-input-searchable--is-active', 'isOpen:form-field-input-searchable--is-open', 'standaloneStyling:form-field-input-searchable--standalone', 'hasEnteredAdditiveOption:form-field-input-searchable--has-entered-custom-value'],
  tabindex: -1,
  attributeBindings: ['tabindex'],
  willDestroyElement() {
    this._super();

    // to clean up state / event handlers if the component is being removed by parent component logic
    if (this.popover) {
      this.popover.removeEventListener('scroll', this.boundHandleScroll);
    }
    this.element.removeEventListener('mouseleave', this._onMouseLeave);
    this.element.removeEventListener('mousemove', this._onIgnoredMouseMove);
    window.removeEventListener('mousedown', this._onIgnoredMouseDown, {
      capture: true
    });
  },
  didInsertElement() {
    this._super();
    const value = this.get('value');
    this.boundHandleScroll = this.onScroll.bind(this);
    if (value) {
      this.set('filter', get(value, this.get('labelPath')));
      this.set('highlightedOption', value);
    }
    this.set('input', this.element.querySelector('.form-field-input-searchable__input'));
    this.set('popover', this.element.querySelector('.form-field-input-searchable__popover'));
    if (this.popover) {
      this.popover.addEventListener('scroll', this.boundHandleScroll);
    }
    this._onMouseLeave = () => this.set('highlightedOption', null);
    this.element.addEventListener('mouseleave', this._onMouseLeave);
    if (this.shouldIgnoreFocusOut()) {
      this._onIgnoredMouseDown = this._onIgnoredMouseDown.bind(this);
      // window capture is important -- draggabilly happens in the capture phase and we need to do it earlier
      window.addEventListener('mousedown', this._onIgnoredMouseDown, {
        capture: true
      });
      this._onIgnoredMouseMove = this._onIgnoredMouseMove.bind(this);
      this.element.addEventListener('mousemove', this._onIgnoredMouseMove);
    }
  },
  didReceiveAttrs() {
    this._super();
    const {
      value,
      previousValue
    } = this.getProperties('value', 'previousValue');
    if (value !== previousValue) {
      if (value) {
        this.set('filter', get(value, this.get('labelPath')));
      } else {
        this.set('filter', '');
      }
    }
    this.set('previousValue', value);
    this._setInitialHighlightedOption();
  },
  _setInitialHighlightedOption() {
    let highlightedOption;
    if (this.get('highlightDefaultOptionEnabled')) {
      highlightedOption = this.get('defaultOption');
    } else if (this.get('highlightFirstOptionEnabled')) {
      highlightedOption = this.get('optionsList')[0];
    } else {
      return;
    }
    this.set('highlightedOption', highlightedOption);
  },
  defaultFilter: computed('value', 'labelPath', function () {
    return this.value ? get(this.value, this.labelPath) : '';
  }),
  highlightDefaultOptionEnabled: computed('defaultOption', 'shouldHighlightDefaultOption', 'optionsList.[]', function () {
    const defaultOption = this.get('defaultOption');
    const shouldHighlightDefaultOption = defaultOption && this.get('shouldHighlightDefaultOption');
    if (shouldHighlightDefaultOption) {
      const renderedOptions = this.get('optionsList');
      if (!(renderedOptions && renderedOptions.includes(defaultOption)) && window.console) {
        // We need to use console.warn() because Ember.Logger.warn is deprecated
        // as of Ember 3.2.
        window.console.warn('Cannot highlight defaultOption; does not exist in the optionsList');
      }
    }
    return shouldHighlightDefaultOption;
  }),
  highlightFirstOptionEnabled: computed('optionsList.[]', 'shouldHighlightFirstOption', function () {
    return !isEmpty(this.get('optionsList')) && this.get('shouldHighlightFirstOption');
  }),
  // internal state
  filter: null,
  isOpen: computed('alwaysOpenIfActive', 'filter.length', 'isActive', 'isLoading', 'optionsList.length', function () {
    return this.get('isActive') && (this.get('filter.length') > 0 || this.get('optionsList.length') > 0 || this.get('isLoading') || this.alwaysOpenIfActive);
  }),
  isActive: false,
  previousValue: reads('value'),
  shouldShowAdditiveOption() {
    /*
       NB: This is intentionally not a computed property.  'optionsList' is a caller,
       which encourages the adding of 'shouldShowAdditiveOption' as a dependency to
       'optionsList'.  It then becomes easy to create a dependency chain on something
       like 'filter' which would invalidate 'optionsList' on every keypress.
        This leads to performance difficulties on lists of options of size, say, 1000.
        Further, 'shouldShowAdditiveOption' doesn't need to be a computed property
       because the properties it could depend on are: 'filter', 'options', 'isAdditive',
       'labelPath'.
        Since changes to 'filter' cause changes to 'options' (newly allocated memory,
       not necessarily different contents), a dependency on 'filter' is redundant.
       'filter' invalidates on every keypress anyways, so this would never return a
       cached result.
        Similarly, since 'optionsList' is the only thing that checks this and 'optionsList'
       already depends on 'options', there's no need for that here either.
        Finally, we only care about changes to 'isAdditive' and 'labelPath' when
       we want to process changes to the list of options.
        This can simply be a function.
    */
    if (!this.get('isAdditive')) {
      return false;
    }
    const filter = this.get('filter');
    if (isBlank(filter)) {
      return false;
    }
    const lowerCaseFilter = filter.toLowerCase();
    const label = this.get('labelPath');

    // don't include additive option if filter has match found in options
    const filterMatchesOption = (this.get('options') || []).filter(isPresent).find(item => {
      const itemName = get(item, label);
      if (isBlank(itemName)) {
        return false;
      }
      return itemName.toLowerCase() === lowerCaseFilter;
    });
    return !filterMatchesOption;
  },
  shouldShowCaret: computed('_shouldShowCaret', 'filter', 'hasEnteredAdditiveOption', 'isOpen', 'isReadOnly', {
    get() {
      if (this._shouldShowCaret !== undefined) {
        return this._shouldShowCaret;
      }
      return !isBlank(this.get('filter')) && !this.get('isOpen') && !this.get('hasEnteredAdditiveOption') && !this.get('isReadOnly');
    },
    set(_, value) {
      return set(this, '_shouldShowCaret', value);
    }
  }),
  optionsList: computed('options.[]', '_internallyFilteredOptions.[]', function () {
    let optionsToRender = [];
    const options = this.get('options');

    // If we've filtered internally using defaultFilter to narrow things down, render that list
    if (this.get('_internallyFilteredOptions')) {
      optionsToRender = this.get('_internallyFilteredOptions');
      // If the component has options passed in, use that list
    } else if (options) {
      optionsToRender = options;
    }
    if (this.shouldShowAdditiveOption() && !options.some(option => option.isAdditive)) {
      return [{
        isAdditive: true
      }, ...optionsToRender];
    } else {
      return optionsToRender;
    }
  }),
  formattedOptions: computed('optionsList.[]', 'groupOptionsBy', function () {
    const groupOptionsBy = this.get('groupOptionsBy');
    const options = this.get('optionsList');

    // If no "groupOptionsBy" header prop is specified
    // If there are no options, make sure to return a single empty group.
    if (isEmpty(groupOptionsBy) || options.length === 0) {
      return [{
        options
      }];
    }

    // Map options to groups by specified property
    const headersMap = uniq(options.map(option => get(option, groupOptionsBy)));

    // Reduce the subsequent "headers" array into an array of groups with
    // headers and options. Sort options with no header to the top of the
    // options list.
    return headersMap.reduce((groups, header) => {
      groups.push({
        header,
        options: options.filter(option => get(option, groupOptionsBy) === header)
      });
      return groups;
    }, []).sort((groupA, groupB) => {
      if (!groupA.header) {
        return -1;
      }
      if (!groupB.header) {
        return 1;
      }
      return 0;
    });
  }),
  shouldHighlightFirstOption: not('defaultOption'),
  highlightedOption: null,
  // event handlers
  keyDown(e) {
    switch (e.keyCode) {
      case KEY_CODE_DOWN:
        this.highlightNext(e);
        break;
      case KEY_CODE_UP:
        this.highlightPrevious(e);
        break;
      case KEY_CODE_ENTER:
        this.enterPressed(e);
        break;
      case KEY_CODE_ESC:
        if (this.get('isActive')) {
          if (this.get('inputCancelled')) {
            this.applyFilter('');
            if (this.inputCancelled) {
              this.inputCancelled();
            }
          } else {
            if (this.componentBlurred) {
              this.componentBlurred();
            }
            if (this.get('syncFilterOnClose')) {
              this.syncFilter();
            }
          }
          this.closeDropdown();
          e.stopPropagation();
        }
        break;
      case KEY_CODE_CONTROL:
        break;
      case KEY_CODE_META:
        break;
      case KEY_CODE_SHIFT:
        break;
      case KEY_CODE_TAB:
        this.closeDropdown();
        break;
      default:
        this.openDropdown();
    }
  },
  // dropdown management
  openDropdown() {
    if (this.get('isActive')) {
      return;
    }
    if (this.dropdownOpened) {
      this.dropdownOpened();
    }
    this.set('isActive', true);
    scheduleOnce('afterRender', this, this.selectSearchableInput);
  },
  selectSearchableInput() {
    this.element.querySelector('.form-field-input-searchable__input').select();
  },
  // Some addons (draggabilly) observe mousedown and manually fires blur events.
  // This breaks how we detect blurring so we need some manual handling of mouse events.
  shouldIgnoreFocusOut() {
    return this.element.closest('.draggable-list,.draggable-list__row') !== null;
  },
  mouseUp() {
    this.ignoreFocusOut = false;
  },
  containsElement(element) {
    return this.element === element || this.element.contains(element);
  },
  _onIgnoredMouseDown(e) {
    if (this.containsElement(e.target)) {
      this.ignoreFocusOut = true;
    }
  },
  _onIgnoredMouseMove() {
    // Forcibly blurs when mouse moves while down. This isn't great experience but it matches draggabilly expectations.
    if (this.ignoreFocusOut) {
      this.ignoreFocusOut = false;
      if (this.containsElement(document.activeElement)) {
        document.activeElement.blur();
      }
      this.focusOut();
    }
  },
  focusOut(e) {
    this._super();
    if (this.isOpen && !this.ignoreFocusOut && !this.containsElement(e?.relatedTarget)) {
      this.closeDropdown();
      if (this.componentBlurred) {
        this.componentBlurred();
      }
      if (this.get('syncFilterOnClose')) {
        this.syncFilter();
      }
    }
  },
  closeDropdown() {
    if (this.dropdownClosed) {
      this.dropdownClosed();
    }

    // This isn't likely to happen, but it's here as a safeguard incase the parent component
    // destroys this component after a selection has been made.
    if (this.isDestroyed) {
      return;
    }
    if (this.clearFilterOnClose && this.filter !== this.defaultFilter) {
      this.set('filter', '');
    }
    this.set('isActive', false);
  },
  // selection management
  commitSelection(option) {
    if (!option) {
      this.set('filter', this.defaultFilter);
      return;
    }
    const selectedAdditiveOption = get(option, 'isAdditive');
    this.set('hasEnteredAdditiveOption', selectedAdditiveOption);
    if (this.get('allowDisabled') && !selectedAdditiveOption && get(option, 'isDisabled')) {
      return;
    }
    if (this.get('isAdditive') && selectedAdditiveOption) {
      // `createNewOption` can either return Promises or values directly
      const newFilterPromise = resolve(this.get('createNewOption')(this.get('filter')));
      newFilterPromise.then(newFilter => {
        if (!isNone(newFilter)) {
          this.set('filter', newFilter);
        }
      });
      return;
    }
    const newValue = get(option, this.get('labelPath'));
    // This is a workaround to a global error thrown during tests where
    // the input value is used for a computation after being read.
    // This deferral shouldn't change behavior from a user's perspective.
    // More debugging is needed to find a nicer solution.
    if (this.deferFilterUpdateOnCommitSelection) {
      schedule('afterRender', this, () => {
        this.set('filter', newValue);
      });
    } else {
      this.set('filter', newValue);
    }
    if (this.valueSelected) {
      this.valueSelected(option);
    }
  },
  enterPressed(e) {
    if (this.get('isOpen')) {
      /*
       * It is possible that the current "highlightedOption" is invalid, eg. the user types and then
       * presses enter before loading finishes. In this case the previous highlighted option will
       * still be stored in the property, and will be selected. We actually want to invalidate the
       * previous highlight option in this case (eg. no longer in the optionsList array) and
       * fall back to the defaultOption (if present).
       */
      const highlightedOption = this.get('highlightedIndex') >= 0 ? this.get('highlightedOption') : null;
      const option = highlightedOption || this.get('defaultOption');
      this.commitSelection(option);
      this.closeDropdown();
      e.stopPropagation();
      e.preventDefault();
    }
  },
  // selection management
  highlightedIndex: computed('optionsList.[]', 'highlightedOption', function () {
    return this.get('optionsList').indexOf(this.get('highlightedOption'));
  }),
  highlightIndex(index) {
    const option = this.get('optionsList')[index];
    this.set('highlightedOption', option);
  },
  highlightNext(e) {
    this.openDropdown();
    if (this.get('highlightedIndex') < 0) {
      this.highlightIndex(0);
      e.stopPropagation();
      e.preventDefault();
      return;
    }
    const nextIndex = this.get('highlightedIndex') + 1;
    if (nextIndex < this.get('optionsList.length')) {
      this.highlightIndex(nextIndex);
      e.stopPropagation();
      e.preventDefault();
      scheduleOnce('afterRender', this, this.trackHighlighted);
    }
  },
  highlightPrevious(e) {
    const previousIndex = this.get('highlightedIndex') - 1;
    if (previousIndex > -1) {
      this.highlightIndex(previousIndex);
      e.stopPropagation();
      e.preventDefault();
      scheduleOnce('afterRender', this, this.trackHighlighted);
    }
  },
  // Sync the filter to the current value.
  // If possible, add the text as a new option.
  // Otherwise, reset the text to the previous value.
  syncFilter() {
    const hasNewValue = !isEmpty(this.get('filter')) && this.filter !== this.defaultFilter;
    if (hasNewValue && this.get('optionsList.length') > 0) {
      this.commitSelection(this.get('optionsList')[0]);
    } else if (this.get('inputChanged')) {
      this.set('filter', this.defaultFilter);
    } else {
      this.applyFilter(this.defaultFilter);
    }

    // When the filter is next opened, all the options should be visible, even if something was
    // already selected.
    this.set('_internallyFilteredOptions', null);
  },
  trackHighlighted() {
    const highlighted = document.querySelector('.form-field-input-searchable__option--is-highlighted');
    if (!this.get('isActive') || !highlighted) {
      return;
    }
    const highlightedOptionHeight = highlighted.offsetHeight;
    const highlightedTop = highlighted.offsetTop;
    const highlightedBottom = highlightedTop + highlightedOptionHeight;

    // todo maximei: find method to find the right highlighted row when multiple inputs are rendered.
    const ancestorPane = highlighted.parentElement?.parentElement;
    if (this.get('scrollNearest') && ancestorPane) {
      ancestorPane.scrollTop = highlightedTop;
      return;
    }
    const pane = this.element.querySelector('.form-field-input-searchable__popover');
    const paneHeight = pane.offsetHeight;
    if (highlightedBottom > paneHeight || highlightedTop < 0) {
      highlighted.scrollIntoView();
    }
  },
  // search/filtering logic

  applyFilter(filter) {
    const options = this.get('options');
    const labelPath = this.get('labelPath');
    const format = text => {
      const lowerCased = text.toLowerCase();
      if (this.get('formatter')) {
        return this.get('formatter').apply(lowerCased);
      } else {
        return lowerCased;
      }
    };
    const formattedInput = format(filter);
    const filterLength = formattedInput.length;
    const filtered = options.filter(option => {
      const value = format(get(option, labelPath));
      return value.slice(0, filterLength) === formattedInput;
    });
    this.setProperties({
      _internallyFilteredOptions: filtered,
      filter
    });
    this._setInitialHighlightedOption();
  },
  onScroll() {
    if (this.get('isLoading')) {
      return;
    }
    if (!this.get('dropdownScrolledToBottom')) {
      return;
    }
    const popover = this.get('popover');
    const remainingScrollHeight = popover.scrollHeight - popover.scrollTop;
    const popoverHeight = popover.clientHeight;

    // When certain browsers are zoomed, subpixels need to be accounted for, so we need to provide a 1 pixel buffer
    // https://bugs.chromium.org/p/chromium/issues/detail?id=890345
    const scrollBarAtBottom = Math.abs(remainingScrollHeight - popoverHeight) <= 1;
    if (scrollBarAtBottom && this.dropdownScrolledToBottom) {
      this.dropdownScrolledToBottom();
    }
  },
  actions: {
    open() {
      if (!this.disabled) {
        this.openDropdown();
      }
    },
    filterChanged(event) {
      const value = event.target.value;
      this.set('filter', value);
      if (this.inputChanged) {
        this.inputChanged(value);
      } else {
        this.applyFilter(value);
      }
      if (this.get('popper')) {
        // Update Popper instance  so it can reposition based on
        // new filtered list height. This is especially important
        // if the list is currently flipped on top of the input.
        this.get('popper').scheduleUpdate();
      }
    },
    optionClicked(option) {
      this.commitSelection(option);
      this.closeDropdown();
    }
  }
});